<script setup lang="ts">

import EditUiElementPropertiesBtn from "@/components/EditView/EditUIElementPropertiesBtn.vue";
import Translator from "@/utils/common/Translator.ts";
import HelpMessage from "@/components/EditView/HelpMessage.vue";
import {useNamaFlagsStore} from "@/stores/nama-flags-store.ts";
import {storeToRefs} from "pinia";
import ClientCache from "@/utils/login/ClientCache.ts";
import {useInputRestrictions} from "@/composables/useInputRestrictions.ts";
import {useCtrlShiftDirection} from "@/composables/useCtrlShiftDirection.ts";
import {useTemplateRef} from "vue";
import FieldWidgetUtil from "@/utils/editView/widgets/FieldWidgetUtil.ts";
import EditScreenField = namaTypes.EditScreenField;
import FieldStyle = namaTypes.FieldStyle;

const props = defineProps<{
  doNotApplyThemeLayouts?: boolean,
  isInsideComposite?: boolean,
  isFieldDisabled?: boolean,
  isFieldRequired?: boolean,
  iconCode?: string,
  entityType?: string,
  field?: EditScreenField,
  title?: string
}>();

const {showHelpMessagesActive} = storeToRefs(useNamaFlagsStore());
const fieldId = props.field?.id ?? '';
const isSpanned = FieldWidgetUtil.isSpanned(props.field)

function getFieldStyle(): FieldStyle {
  const fieldStyle = ClientCache.getFieldStyle(props.entityType ?? '', fieldId);
  if (!fieldStyle)
    return {};
  return fieldStyle;
}

const backColor = getFieldStyle().backColor;
const foreColor = getFieldStyle().foreColor;
const direction = getFieldStyle().direction;
const disableSuggestionProvider = getFieldStyle().disableSuggestionProvider;
const doNotGoToNextWithEnter = getFieldStyle().doNotGoToNextWithEnter;
const listColumnDirection = getFieldStyle().listColumnDirection;
const showAsPassword = getFieldStyle().showAsPassword;
const suggestionDirection = getFieldStyle().suggestionDirection;

const containerRef = useTemplateRef<HTMLElement>('containerRef');
useInputRestrictions(containerRef, getFieldStyle().fixedInputLanguage, getFieldStyle().fixedLetterCase);
useCtrlShiftDirection(containerRef);

const containerStyle = {
  '--input-bg': backColor,
  '--input-color': foreColor || undefined,
  '--input-direction': direction,
  '--input-listColumnDirection': listColumnDirection,
  '--input-suggestionDirection': suggestionDirection,
}
</script>

<template>
  <div
    ref="containerRef"
    :style="containerStyle"
    :data-field-disabled="isFieldDisabled?true:undefined"
    class="tw-w-full tw-flex tw-flex-col tw-h-[inherit]"
    :class="{'horizontal:tw-px-0 horizontal:tw-pl-2 horizontal:tw-flex-row  horizontal:tw-items-center tw-px-4 compact:tw-px-0.5 ultra:tw-px-0':(!isInsideComposite && !doNotApplyThemeLayouts), 'tw-px-4 compact:tw-px-0.5 ultra:tw-px-0':(!isInsideComposite && doNotApplyThemeLayouts)}">
    <div v-if="!field?.hideTitle"
         class="tw-py-0 tw-flex tw-items-center tw-w-fit tw-gap-1 tw-justify-end"
         :class="{'horizontal:tw-flex-none':!doNotApplyThemeLayouts,'horizontal:tw-w-[25%] horizontal:quad:tw-w-[30%]': (!doNotApplyThemeLayouts && !isSpanned), 'horizontal:tw-w-[12.5%] horizontal:quad:tw-w-[7.5%] mobile:horizontal:tw-w-[25%] mobile:horizontal:quad:tw-w-[30%]':(!doNotApplyThemeLayouts && isSpanned),'!tw-w-fit':isInsideComposite && showHelpMessagesActive}">
      <EditUiElementPropertiesBtn :blockOrField="field" :entity-type="entityType"/>
      <HelpMessage v-if="showHelpMessagesActive" :entity-type="entityType" :field-id="fieldId" :class="{'tw-mb-1':isInsideComposite}"/>
      <label :for="fieldId"
             v-if="!isInsideComposite"
             class="tw-px-1  tw-text-sm tw-pb-[3px] tw-font-normal tw-flex tw-items-center tw-gap-2"
             :class="{'horizontal:tw-text-end horizontal:tw-text-base horizontal:tw-pb-0 horizontal:tw-justify-end':!doNotApplyThemeLayouts, 'tw-text-gray-400':isFieldDisabled,'tw-text-namaFontColor-label-light dark:tw-text-namaFontColor-label-dark':!isFieldDisabled}">
        <slot name="icon"/>
        <span v-text="title || Translator.translateTitledId(entityType, field)"
              class="[overflow-wrap:anywhere] tw-max-w-[inherit] [word-break:normal] tw-grow"/>
        <span v-text="isFieldRequired?'*':''" class="tw-text-[#DA1414] dark:tw-text-[#ff5050]"/>
      </label>
    </div>
    <div class="tw-h-[inherit]"
         :data-field-id="fieldId"
         :class="{'horizontal:tw-flex-grow':(!isInsideComposite && !doNotApplyThemeLayouts), 'horizontal:tw-w-[75%] horizontal:quad:tw-w-[70%]':(!isInsideComposite && !doNotApplyThemeLayouts && !isSpanned),'tw-w-[inherit]':isInsideComposite , 'horizontal:tw-w-[87.5%] horizontal:quad:tw-w-[92.5%] ':(!isInsideComposite && !doNotApplyThemeLayouts && isSpanned)}">
      <slot name="widget" :showAsPassword="showAsPassword" :doNotGoToNextWithEnter="doNotGoToNextWithEnter" :disableSuggestionProvider="disableSuggestionProvider"/>
    </div>
  </div>
</template>

<style scoped>
:deep(input:not([readonly])) {
  background: var(--input-bg);
  color: var(--input-color, var(--nama-font-color));
  border-radius: 6px;
  direction: var(--input-direction);
}

:deep(.q-field--outlined .q-field__control:before) {
  background: var(--input-bg);
  border-radius: 6px;
}

:deep(input[readonly]) {
  border-radius: 6px;
  background: color-mix(in srgb, var(--input-bg) 65%, white);
}

:deep(.q-checkbox .q-checkbox__bg) {
  border-color: var(--input-bg, var(--nama-checkbox-color));
}

:deep(.q-checkbox) {
  color: var(--input-color, var(--nama-font-color));
}
</style>
